import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';
import { api, type Money, type Valuation } from '../lib/api.js';
import { MoneyText, formatExact } from '../components/MoneyText.js';
import { Card, ErrorNote, Row, Skeleton } from './Home.js';

/**
 * One holding, in full (docs/SCREEN-SPECS.md §3).
 *
 * The third and last tap from Home — Home → Wealth → this — which satisfies
 * Navigation Law 2 (nothing more than three taps away). There is no visible
 * breadcrumb trail: lineage is carried by the back link, the URL, and the
 * member's name in the header (Law 3).
 *
 * Every number here is computed server-side. This screen formats and lays out;
 * it never derives a value, because money arithmetic in the browser is how
 * two screens start disagreeing about the same holding.
 */

interface HoldingDetailDto {
  id: string;
  member: { id: string; name: string; unmatched: boolean };
  scheme: { name: string; isin: string | null; type: 'mf' | 'equity' };
  /** null = no readable statement has said; [] = none registered. */
  nominees: string[] | null;
  lastSeen: string | null;
  folio: string;
  folioFull: string;
  campaignId: string | null;
  status: string;
  units: number | null;
  invested: Money | null;
  valuation: Valuation | null;
  gain: { money: Money; percent: number | null } | null;
  xirrPercent: number | null;
  history: { asOf: string; units: number | null; value: Money; invested: Money | null }[];
  transactions: {
    date: string;
    type: string;
    units: number | null;
    amount: Money;
    nav: Money | null;
    description: string | null;
  }[];
}

export function HoldingDetail() {
  const { id } = useParams<{ id: string }>();
  const { data, isPending, error } = useQuery({
    queryKey: ['holding', id],
    queryFn: () => api.get<HoldingDetailDto>(`/holdings/${id}`),
    enabled: Boolean(id),
  });

  if (isPending) return <Skeleton />;
  if (error) return <ErrorNote message={(error as Error).message} />;

  return (
    <div>
      {/* The back link IS the breadcrumb (Navigation Law 3). */}
      <Link to="/wealth" style={{ font: 'var(--type-subhead)', color: 'var(--color-tint)', textDecoration: 'none' }}>
        ‹ Wealth
      </Link>

      <h1 className="page-title" style={{ font: 'var(--type-title1)', margin: 'var(--space-2) 0 var(--space-1)' }}>
        {data.scheme.name || data.scheme.isin || 'Holding'}
      </h1>
      <p className="page-sub">
        {data.member.name} · {data.scheme.type === 'mf' ? 'Mutual fund' : 'Stock'} · folio {data.folioFull}
        {data.scheme.isin && ` · ${data.scheme.isin}`}
      </p>

      {data.status === 'pending_review' && (
        <p
          className="state-warn"
          style={{
            font: 'var(--type-subhead)',
            border: '1px solid var(--color-status-warning)',
            borderRadius: 'var(--radius-card)',
            padding: 'var(--space-3)',
            margin: '0 0 var(--space-4)',
          }}
        >
          ⚠ This holding did not appear in the latest statement. It has not been removed — check
          whether it was sold or simply absent from the document.
        </p>
      )}

      <div style={{ marginBottom: 'var(--space-6)' }}>
        {data.valuation ? (
          <>
            <MoneyText variant="hero" value={data.valuation} />
            <p className="page-sub" style={{ marginBottom: 0 }}>
              as of {data.valuation.asOf.slice(0, 10)}
              {data.valuation.source === 'nav' && ' · priced from today’s NAV'}
              {data.valuation.source === 'cas' && ' · from the statement'}
            </p>
          </>
        ) : (
          <span className="state-muted">No valuation yet</span>
        )}
      </div>

      <Card title="Position">
        <Row
          left="Units"
          right={
            data.units === null ? (
              <span className="state-muted">—</span>
            ) : (
              // Units are a quantity, not money — MoneyText would prefix a ₹.
              <span className="money-cell">
                {data.units.toLocaleString(undefined, { maximumFractionDigits: 4 })}
              </span>
            )
          }
        />
        <Row
          left="Invested"
          right={data.invested ? <MoneyText value={data.invested} /> : <span className="state-muted">—</span>}
        />
        {/*
          Nominee, with the two kinds of blank kept apart: "None registered" is a
          finding worth acting on, "Not stated" merely means no statement we can
          read has mentioned it. Collapsing them into one dash would turn a
          missing nomination into something the screen appears to have checked.
        */}
        <Row
          left="Nominee"
          right={
            data.nominees === null ? (
              <span className="state-muted">Not stated</span>
            ) : data.nominees.length === 0 ? (
              <span className="badge badge-warn">None registered</span>
            ) : (
              <span>{data.nominees.join(', ')}</span>
            )
          }
        />
        {/*
          When a statement last confirmed this holding. A holding that stopped
          being reported does not look wrong in a total — the total simply stops
          moving — so the date has to be visible next to the figure.
        */}
        {data.lastSeen && <Row left="Last confirmed" right={<span>{data.lastSeen.slice(0, 10)}</span>} />}
        {data.xirrPercent !== null && (
          <Row
            left={
              <span>
                Return
                <span className="row-detail">annualised, money-weighted</span>
              </span>
            }
            right={
              <span className={`money-cell ${data.xirrPercent >= 0 ? 'delta-pos' : 'delta-neg'}`}>
                {/* Glyph as well as colour, always (DESIGN-SYSTEM §2.2). */}
                {data.xirrPercent >= 0 ? '▲' : '▼'} {Math.abs(data.xirrPercent).toFixed(1)}%
              </span>
            }
          />
        )}
        {data.gain && (
          <Row
            left="Gain"
            right={
              <span>
                <MoneyText value={data.gain.money} signed />
                {data.gain.percent !== null && (
                  <span className="state-muted" style={{ font: 'var(--type-footnote)', marginLeft: 8 }}>
                    {data.gain.percent >= 0 ? '+' : '−'}
                    {Math.abs(data.gain.percent).toFixed(1)}%
                  </span>
                )}
              </span>
            }
          />
        )}
      </Card>

      {/*
        Statement positions, newest first. Deliberately a list and not a chart:
        these are discrete observations on the dates statements were issued, and
        a line between them would imply values we never measured. The chart layer
        arrives with the dataviz pass, and will need to say the same truth.
      */}
      {data.history.length > 1 && (
        <Card title="Value at each statement">
          {[...data.history].reverse().map((point) => (
            <Row
              key={point.asOf}
              left={<span style={{ font: 'var(--type-subhead)' }}>{point.asOf}</span>}
              right={<MoneyText value={point.value} />}
            />
          ))}
        </Card>
      )}

      {data.transactions.length > 0 && (
        <Card title={`Transactions (${data.transactions.length})`}>
          {data.transactions.map((txn, i) => (
            <Row
              key={`${txn.date}-${i}`}
              left={
                <span>
                  <span style={{ display: 'block', font: 'var(--type-subhead)' }}>
                    {txn.description || txn.type}
                  </span>
                  <span className="row-detail">
                    {txn.date}
                    {txn.units !== null &&
                      ` · ${txn.units.toLocaleString(undefined, { maximumFractionDigits: 3 })} units`}
                    {txn.nav && ` @ ${formatExact(txn.nav.amount)}`}
                  </span>
                </span>
              }
              right={<MoneyText value={txn.amount} signed />}
            />
          ))}
        </Card>
      )}

      {data.history.length <= 1 && data.transactions.length === 0 && (
        <Card title="History">
          <div className="empty">
            <span className="empty-glyph" aria-hidden>
              ◷
            </span>
            Only one statement so far. Upload another and the change will show here.
          </div>
        </Card>
      )}
    </div>
  );
}
